import React, { useEffect, useState } from 'react';
import { Button, alpha } from '@mui/material';
import jwtAxios from '@projectK/services/auth/JWT';
import PropTypes from 'prop-types';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { useToast } from '@projectK/context/ToastProvider';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import {
  timeZoneDate,
  formatTime,
} from '@projectK/modules/apps/DateFormat';
import CompanyTheme from '@projectK/constants/CompanyTheme';
 
import { UpcomingActivitiesMain } from '@projectK/MainComponents';
import { isParent } from '@projectK/context/RolesProvider';
import RestrictedPopupDialog from '@projectK/modules/apps/RestrictedPopupDialog';
const themeColors = [
  '#2563EB', // blue-600
  '#9333EA', // purple-600
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#3B82F6', // blue-500
  '#8B5CF6', // violet-500
  '#14B8A6', // teal-500
  '#84CC16', // lime-500
  '#F97316', // orange-500
];
 
// 🔹 Debounce Hook
function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);
 
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
 
  return debouncedValue;
}
 
function isStartDateAfterToday(data) {
  const startDateString = data?.start_date?.substring(0, 10);
  const startTime = data.start_time;
  const startDateTimeString = `${startDateString}T${startTime}`;
  const startDate = timeZoneDate(startDateTimeString).startDate;
  const now = timeZoneDate().todayDate;
  return startDate > now;
}
 
function renderSignupButton(data, handleSignUp, companyTheme,loading) {
  const userId = data?.child_id || data?.user_id;
  return (
    <Button
      variant='contained'
      size='small'
      disabled={loading}
      sx={{
        height: '25px',
        backgroundColor: companyTheme?.primary?.main || '#2196f3',
        '&:hover': {
          backgroundColor: companyTheme
            ? alpha(companyTheme?.primary?.main, 0.8)
            : alpha('#2196f3', 0.8),
        },
         borderRadius:'15px'
      }}
      onClick={() => handleSignUp(userId, data.id, data.productId)}
    >
      Signup
    </Button>
  );
}
 
function renderWaitlistButton(data, companyThemeColor, handleWaitlistFunc,loading) {
  return (
    <Button
      variant='outlined'
      size='small'
      disabled={loading}
      sx={{
        height: '25px',
        borderColor: companyThemeColor
          ? companyThemeColor.primary.main
          : '#2196f3',
        '&:hover': {
          borderColor: companyThemeColor
            ? alpha(companyThemeColor.primary.main, 0.8)
            : alpha('#2196f3', 0.8),
        },
        color: companyThemeColor ? companyThemeColor.primary.main : 'primary',
      }}
      onClick={() =>
        handleWaitlistFunc(
          data?.child_id || data?.user_id,
          data.id,
          data.productId,
        )
      }
    >
      Waitlist
    </Button>
  );
}
 
function renderActionButton(
  data,
  companyThemeColor,
  handleSignUp,
  handleWaitlistFunc,
  loading,
) {
  const canSignUp = data?.user_session_count < data?.max_attendees;
  const isWaitlistEnabled = !data?.disable_waitlist;
  const isDateAllowed = isStartDateAfterToday(data);
  if (isDateAllowed) {
    if (canSignUp) {
      return renderSignupButton(data, handleSignUp, companyThemeColor,loading);
    } else if (isWaitlistEnabled) {
      return renderWaitlistButton(data, companyThemeColor, handleWaitlistFunc,loading);
    }
  }
 
  return null; // Or any other fallback JSX
}
 
function getSessionTime(data) {
  if (data.start_time && data.end_time) {
    return (
      <>
        {formatTime(data.start_time)} - {formatTime(data.end_time)}
      </>
    );
  }
  return null;
}
 
const UpcomingActivities = ({
  list,
  setList,
  loading,
  setLoading,
  setDateFilter,
  setSearchQuery,
  setShowAll,
  showAll,
  useDateFilter,
  fetchActivitySessions,
  fetchMySchedule,
  fetchWaitList,
  fetchUserProduct,
  fetchNext7DaysSessionCount,
}) => {
  const companyTheme = CompanyTheme();
  const {user}=useAuthUser();
  const [view, setView] = React.useState('list');
  const { isMobile, mobileMode } = useMobileSettings();
  const infoViewActionsContext = useInfoViewActionsContext();
  const showToast = useToast();
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const handleSignUp = async (user_id, id, product_id) => {
    try {
      setLoading(true);
      // Make the POST request using jwtAxios
      await jwtAxios.post(`/user-session`, {
        user_id: user_id,
        activity_session_id: id,
        product_id: product_id,
      });
      setLoading(false);
      fetchActivitySessions();
      fetchMySchedule();
      fetchUserProduct();
      if (!isMobile) {
        infoViewActionsContext.showMessage(
          `Signed up to session successfully.`,
        );
        fetchNext7DaysSessionCount();
      } else {
        showToast('Signed up to session successfully.', 'success', mobileMode);
      }
      const isParentUser = isParent(user);
      jwtAxios.get(
        `/events/update/user/calender`,
        isParentUser
          ? {
              params: {
                user_id: user_id,
              },
            }
          : {},
      );
    } catch (error) {
      setLoading(false);
      if (error.status === 423) {
        setIsErrorDialogOpen(true);
      } else if (isMobile) {
        showToast(error?.response?.data?.error, 'error', mobileMode);
      } else {
        infoViewActionsContext.fetchError(error?.response?.data?.error);
      }
    }
  };
 
  const handleWaitlist = async (user_id, id, product_id) => {
    try {
      setLoading(true);
      // Make the POST request using jwtAxios
      await jwtAxios.post(`/user-waitlist`, {
        user_id: user_id,
        activity_session_id: id,
        product_id: product_id,
      });
      setLoading(false);
      fetchActivitySessions();
      fetchMySchedule();
      fetchUserProduct();
      if (!isMobile) {
        infoViewActionsContext.showMessage(
          `Session added to waitlist successfully.`,
        );
      } else {
        showToast(
          'Session added to waitlist successfully.',
          'success',
          mobileMode,
        );
      }
      fetchWaitList();
    } catch (error) {
      setLoading(false);
      if (error.status === 423) {
        setIsErrorDialogOpen(true);
      } else if (isMobile) {
        showToast(error?.response?.data?.error, 'error', mobileMode);
      } else {
        infoViewActionsContext.fetchError(error?.response?.data?.error);
      }
    }
  };
  const [searchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const onSearchActivity = (e) => {
    setSearchQuery(e.target.value);
    setList([]);
    setLoading(true);
  };
 
  // Debounce effect
  useEffect(() => {
    if (debouncedSearch !== '') {
      setLoading(true);
      setSearchQuery(debouncedSearch);
    }
  }, [debouncedSearch]);
 
  const toggleShowAll = () => {
    setShowAll(!showAll);
    setDateFilter(!useDateFilter);
  }; 
  return (
    <>
      <UpcomingActivitiesMain
        list={list}
        loading={loading}
        onSearchActivity={onSearchActivity}
        setView={setView}
        view={view}
        themeColors={themeColors}
        renderActionButton={renderActionButton}
        companyTheme={companyTheme}
        handleSignUp={handleSignUp}
        handleWaitlist={handleWaitlist}
        getSessionTime={getSessionTime}
        user={user}
        toggleShowAll={toggleShowAll}
        showAll={showAll}
        fetchNext7DaysSessionCount={fetchNext7DaysSessionCount}
        fetchSettings={fetchActivitySessions}
        fetchMySchedule={fetchMySchedule}
        fetchUserProduct={fetchUserProduct}
      />
      {isErrorDialogOpen && (
        <RestrictedPopupDialog
          setIsErrorDialogOpen={setIsErrorDialogOpen}
          isErrorDialogOpen={isErrorDialogOpen}
        />
      )}
    </>
  );
};
 
export default UpcomingActivities;
 
UpcomingActivities.propTypes = {
  list: PropTypes.array,
  setList: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setDateFilter: PropTypes.func,
  setSearchQuery: PropTypes.func,
  setShowAll: PropTypes.func,
  showAll: PropTypes.bool,
  useDateFilter: PropTypes.bool,
  fetchActivitySessions: PropTypes.func,
  fetchMySchedule: PropTypes.func,
  fetchWaitList: PropTypes.func,
  fetchUserProduct: PropTypes.func,
  fetchNext7DaysSessionCount: PropTypes.func,
};
